/**
 * DEV Overrides Badge
 * 
 * Global badge shown when DEV agent overrides are active.
 * Should be placed in the admin layout for visibility.
 */

import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { FlaskConical, Settings2 } from 'lucide-react';
import { areDevOverridesActive, getDevOverridesSummary } from '@/lib/dev/agentOverrides';
import { arePerAgentOverridesActive, getOverriddenAgentCount, getPerAgentOverridesSummary } from '@/lib/dev/perAgentOverrides';
import { isDevMode } from '@/lib/featureFlags';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export function DevOverridesBadge() {
  const [isGlobalActive, setIsGlobalActive] = useState(false);
  const [isPerAgentActive, setIsPerAgentActive] = useState(false);
  const [globalSummary, setGlobalSummary] = useState<string[]>([]);
  const [perAgentCount, setPerAgentCount] = useState(0);
  const [perAgentSummary, setPerAgentSummary] = useState<{ agentId: string; summary: string[] }[]>([]);
  
  // Poll for changes since overrides are mutable
  useEffect(() => {
    if (!isDevMode) return;
    
    const checkState = () => {
      setIsGlobalActive(areDevOverridesActive());
      setGlobalSummary(getDevOverridesSummary());
      setIsPerAgentActive(arePerAgentOverridesActive());
      setPerAgentCount(getOverriddenAgentCount());
      setPerAgentSummary(getPerAgentOverridesSummary());
    };
    
    checkState();
    const interval = setInterval(checkState, 500);
    
    return () => clearInterval(interval);
  }, []);
  
  const isActive = isGlobalActive || isPerAgentActive;
  
  if (!isDevMode || !isActive) {
    return null;
  }
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="outline" 
            className="bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500 animate-pulse cursor-help"
          >
            {isPerAgentActive ? (
              <Settings2 className="h-3 w-3 mr-1" />
            ) : (
              <FlaskConical className="h-3 w-3 mr-1" />
            )}
            DEV OVERRIDES ACTIVE
            {isPerAgentActive && ` (${perAgentCount} agent${perAgentCount !== 1 ? 's' : ''})`}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-sm">
          {isGlobalActive && (
            <>
              <p className="font-medium mb-1">Global Overrides:</p>
              <ul className="text-xs space-y-0.5">
                {globalSummary.length > 0 ? (
                  globalSummary.map((item, i) => (
                    <li key={i}>• {item}</li>
                  ))
                ) : (
                  <li>• Enabled (no specific overrides)</li>
                )}
              </ul>
            </>
          )}
          {isPerAgentActive && (
            <>
              <p className="font-medium mb-1 mt-2">Per-Agent Overrides:</p>
              <ul className="text-xs space-y-0.5">
                {perAgentSummary.slice(0, 3).map(({ agentId, summary }) => (
                  <li key={agentId}>
                    • {agentId.substring(0, 10)}...: {summary.join(', ')}
                  </li>
                ))}
                {perAgentSummary.length > 3 && (
                  <li className="text-muted-foreground">
                    ...and {perAgentSummary.length - 3} more
                  </li>
                )}
              </ul>
            </>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            These overrides affect routing eligibility in development only.
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
